/**
 * sustainabilityService.js — Sürdürülebilirlik Beyanı Formu Servisi
 *
 * Backend henüz hazır değil → localStorage tabanlı mock çalışıyor.
 * USE_MOCK_DATA = false yapıldığında gerçek API'ye geçer.
 *
 * Planlanan endpoint'ler (backend hazır olunca):
 *   POST   /api/v1/sustainability/submissions        → form gönder
 *   GET    /api/v1/sustainability/submissions?fuarId=&page=&limit=  → liste
 *   GET    /api/v1/sustainability/submissions/{id}   → detay
 *   DELETE /api/v1/sustainability/submissions/{id}   → sil
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

// Backend hazır olunca false yapın
export const USE_MOCK_DATA = true;

const STORAGE_KEY = 'fds_sustainability_submissions';
const API_BASE = `${JAVA_BACKEND_API_URL}/sustainability/submissions`;

// ─── Local Storage Yardımcıları ───────────────────────────────────────────────
function loadAll() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveAll(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function generateId() {
  return 'sus_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
}

// ─── Mock Uygulama ─────────────────────────────────────────────────────────────
const mock = {
  submit(data) {
    const items = loadAll();
    const entry = {
      ...data,
      id: generateId(),
      createdAt: new Date().toISOString(),
      submittedBy: localStorage.getItem('userId') || null,
    };
    items.push(entry);
    saveAll(items);
    return { id: entry.id, createdAt: entry.createdAt };
  },

  getAll({ fuarId, page = 1, limit = 25, search = '' } = {}) {
    let items = loadAll();
    if (fuarId) items = items.filter(i => String(i.fuarId) === String(fuarId));
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(i =>
        (i.exhibitorName || '').toLowerCase().includes(q) ||
        (i.standNumber   || '').toLowerCase().includes(q)
      );
    }
    items = [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const total = items.length;
    const start = (page - 1) * limit;
    return { data: items.slice(start, start + limit), total, page, limit };
  },

  getById(id) {
    return loadAll().find(i => i.id === id) || null;
  },

  delete(id) {
    const items = loadAll().filter(i => i.id !== id);
    saveAll(items);
  },
};

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Formu kaydet.
 * @param {object} formData — tüm form alanları
 */
export async function submitSustainabilityForm(formData) {
  if (USE_MOCK_DATA) {
    return mock.submit(formData);
  }
  const res = await axios.post(API_BASE, formData, { headers: getAuthHeader() });
  return res.data;
}

/**
 * Submission listesi.
 */
export async function getSustainabilitySubmissions({ fuarId, page = 1, limit = 25, search = '' } = {}) {
  if (USE_MOCK_DATA) {
    return mock.getAll({ fuarId, page, limit, search });
  }
  const res = await axios.get(API_BASE, {
    params: { fuarId, page, limit, search },
    headers: getAuthHeader(),
  });
  return res.data; // { data: [...], total, page, limit }
}

/**
 * Tekil submission detayı.
 */
export async function getSustainabilitySubmission(id) {
  if (USE_MOCK_DATA) {
    return mock.getById(id);
  }
  const res = await axios.get(`${API_BASE}/${id}`, { headers: getAuthHeader() });
  return res.data;
}

/**
 * Submission sil.
 */
export async function deleteSustainabilitySubmission(id) {
  if (USE_MOCK_DATA) {
    mock.delete(id);
    return;
  }
  await axios.delete(`${API_BASE}/${id}`, { headers: getAuthHeader() });
}

